import type { Socket } from "socket.io-client"
import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconEnvelopePlusFill18,
	IconImageFill18,
	IconMessagesFill18,
} from "nucleo-ui-fill-18"
import { IconEnvelopeCheckOutline18 as MailCheckIcon } from "nucleo-ui-outline-18"

import type { EmailContact } from "@productlane/api"
import type { FormattedAttachment } from "@productlane/ui"
import type { CreateEmailArgs } from "@productlane/zero/types"
import { generateId } from "@productlane/lib/src/browser"
import { invalidRecipientEmails } from "@productlane/lib/src/email-domain"
import {
	hasEnabledEmail,
	primaryAddress,
} from "@productlane/lib/src/email-settings"
import { prepareComposerHtmlForEmail } from "@productlane/lib/src/html"
import {
	IMPORTED_SLACK_REPLY_MESSAGE,
	isImportedSlackChannel,
} from "@productlane/lib/src/imported-thread"
import { SlackIcon } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ReplyType, TabType } from "./types"
import type { Attachment } from "@/screens/app/threads/hooks/useEditorState"
import type { Editor } from "@tiptap/react"
import { toast } from "@/components/toast"
import { useUndoContext } from "@/context/undo-context"
import { api } from "@/lib/api"
import { sendLiveChatReply } from "@/lib/send-live-chat-reply"
import { useSession } from "@/lib/session"
import { joinSessionAndWait } from "@/lib/sockets/live-chat"
import { useLiveChatStore } from "@/lib/states/live-chat-state"
import { useUnsentMessages } from "@/lib/unsent-messages"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { useThreadContext } from "../../../thread-context"
import {
	convertToEmailAttachments,
	convertToSlackAttachments,
} from "../../file-upload/util"
import { findUnsendableAttachment } from "./attachment-send-guard"
import { sendChannel } from "./reply-channel"
import { hasUnuploadedInlineImage } from "./send-guards"
import { useMessageUndo } from "./use-message-undo"

interface UseSendMessageParams {
	composeEditor: Editor | null
	commentEditor: Editor | null
	activeTab: TabType
	replyType: ReplyType
	isForwardMode: boolean
	forwardRecipients: EmailContact[]
	ccRecipients: EmailContact[]
	bccRecipients: EmailContact[]
	attachments: Attachment[]
	formattedAttachments: FormattedAttachment[]
	commentAttachments: Attachment[]
	reset: () => void
	resetComment: () => void
	updateContent: (content: string) => void
	setIsReply: (v: boolean) => void
	setIsComment: (v: boolean) => void
	setHasClickedReply: (v: boolean) => void
	setIsForwardMode: (v: boolean) => void
	setForwardRecipients: (v: EmailContact[]) => void
	clearReplyRecipients: () => void
	fromAddressId: string | null
}

export function useSendMessage({
	composeEditor,
	commentEditor,
	activeTab,
	replyType,
	isForwardMode,
	forwardRecipients,
	ccRecipients,
	bccRecipients,
	attachments,
	formattedAttachments,
	commentAttachments,
	reset,
	resetComment,
	updateContent,
	setIsReply,
	setIsComment,
	setHasClickedReply,
	setIsForwardMode,
	setForwardRecipients,
	clearReplyRecipients,
	fromAddressId,
}: UseSendMessageParams) {
	const navigate = useNavigate()
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { session } = useSession()
	const { thread } = useThreadContext()
	const { liveChatSocket, connectToLiveChat } = useLiveChatStore()
	const openLiveChannel = api.thread.openLiveChannel.useMutation()

	const currentLiveChat = thread.liveChatSession

	const slackMessages = useMemo(
		() => thread.slackMessages ?? [],
		[thread.slackMessages],
	)

	const latestSlackMessage = useMemo(
		() =>
			slackMessages.length > 0
				? [...slackMessages].sort((a, b) => b.createdAt - a.createdAt)[0]
				: undefined,
		[slackMessages],
	)

	const slackQuery = api.workspace.slackSettings.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
		enabled: thread.origin === "SLACK_CONNECT",
	})

	const reactivateEmail = api.mail.reactivateContactEmail.useMutation({
		onSuccess: (result) => {
			if (result.reactivated) {
				toast.success({ title: "Email reactivated. You can reply now." })
				return
			}
			if (result.reason === "spam_complaint") {
				toast.error({
					title: "Cannot reactivate automatically",
					description:
						"This address was suppressed by a spam complaint. The recipient must confirm they want emails again, then contact Postmark support to reactivate it.",
				})
				return
			}
			toast.error({ title: "Could not reactivate this email" })
		},
		onError: () => {
			toast.error({ title: "Could not reactivate this email" })
		},
	})

	const isNewSlackConnected =
		slackQuery.data?.connected && slackQuery.data.isNewIntegration

	const title = useMemo(() => {
		const baseTitle = thread.title || "Feedback"
		if (isForwardMode) {
			const cleanTitle = baseTitle.replace(/^(Re:|Fwd:)\s*/i, "")
			return `Fwd: ${cleanTitle}`
		}
		return baseTitle.startsWith("Re:") ? baseTitle : `Re: ${baseTitle}`
	}, [thread.title, isForwardMode])

	const { sendPendingMessage, getUndoMessageFn } = useMessageUndo(
		(content: string) => {
			setIsReply(true)
			composeEditor
				?.chain()
				.setContent(content)
				.focus("end", { scrollIntoView: false })
				.run()
			updateContent(content)
		},
	)
	const { pendingMessages, getMessageContent } = useUndoContext()

	const scrollNewEventIntoView = () => {
		// ScrollViewport uses flex-col-reverse, so scrollTop=0 is the visual bottom
		// (where the just-sent message renders).
		//
		// Two RAFs: the first gives React a frame to commit the optimistic Zero
		// mutation to the DOM; the second gives layout a frame to settle after
		// the bottom bar shrinks (editor clears on send). Without this the
		// scroll lands before the new event has measured into the layout.
		//
		// Instant scroll (direct scrollTop assignment) instead of smooth: a
		// ResizeObserver on the viewport rewrites scrollTop when the bottom bar
		// resizes, which would interrupt a smooth animation mid-flight and
		// leave the new message partly behind the bottom bar. The entry
		// animation on the message itself supplies the visual interest.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const scrollArea = document.getElementById("messages")
				if (scrollArea) {
					scrollArea.scrollTop = 0
				}
			})
		})
	}

	// Runs after every guard and before the message is written, so the
	// self-assigned event sits above the reply instead of popping in below it.
	const claimUnassignedThread = async () => {
		if (thread.assigneeId || !session?.userId) return
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					assigneeId: session.userId,
					assigneeActivityId: generateId(),
				}),
			),
		)
	}

	const warnEmailSetupMissing = () => {
		toast.info({
			title: "Email setup required",
			description:
				"Configure email settings to enable follow-up emails when customers don't read your messages.",
			action: {
				label: "Configure Email",
				callback: () => {
					void navigate({ to: "/settings/emails" })
				},
			},
		})
	}

	const resolveLiveChannel = async (): Promise<
		{ socket: Socket; sessionId: string } | "email" | null
	> => {
		if (currentLiveChat?.id) {
			if (!liveChatSocket?.connected) {
				toast.error({ title: "Not connected to Live chat." })
				return null
			}
			if (!hasEnabledEmail(workspace?.emailSettings)) {
				warnEmailSetupMissing()
				return null
			}
			return { socket: liveChatSocket, sessionId: currentLiveChat.id }
		}

		if (!hasEnabledEmail(workspace?.emailSettings)) {
			warnEmailSetupMissing()
			return null
		}

		const { liveChatSessionId, reason } = await openLiveChannel.mutateAsync({
			threadId: thread.id,
		})
		if (!liveChatSessionId && reason === "no-chat") return "email"
		if (!liveChatSessionId) {
			toast.error({
				title: "Live chat is off for this workspace",
				description:
					"Turn on live chat in widget settings to answer in the chat, or reply by email instead.",
				icon: IconMessagesFill18,
				action: {
					label: "Widget settings",
					callback: () => {
						void navigate({ to: "/settings/widget" })
					},
				},
			})
			return null
		}

		const socket = connectToLiveChat(liveChatSessionId, {
			name: session?.name,
			imageUrl: session?.imageUrl || undefined,
			userId: session?.userId,
		})
		if (!socket) {
			toast.error({ title: "Not connected to Live chat." })
			return null
		}

		const joined = await joinSessionAndWait(socket, liveChatSessionId, {
			name: session?.name,
			imageUrl: session?.imageUrl || undefined,
			userId: session?.userId,
		})
		if (!joined) {
			toast.error({ title: "Not connected to Live chat." })
			return null
		}

		return { socket, sessionId: liveChatSessionId }
	}

	const send = async () => {
		if (session?.membershipRole === "VIEWER" && activeTab !== "comment") {
			toast.info({
				title: "Upgrade Required",
				description:
					"Viewers cannot send messages. Please contact your workspace owner to upgrade your role.",
			})
			return
		}
		const activeAttachments =
			activeTab === "comment" ? commentAttachments : attachments
		const unsendable = findUnsendableAttachment(activeAttachments)
		if (unsendable === "loading") {
			toast.info({
				title: "Attachments are still uploading. Send again once they finish.",
			})
			return
		}
		if (unsendable === "missing-url") {
			toast.info({
				title: "An attachment failed to upload. Remove it or add it again.",
			})
			return
		}
		if (activeTab === "comment") {
			if (commentEditor?.isEmpty && !commentAttachments.length) {
				return
			}
			await mutateOk(
				zero.mutate(
					mutators.comments.create({
						id: generateId(),
						content: commentEditor?.getHTML() ?? "",
						feedbackId: thread.id,
						userId: session?.userId ?? "",
						attachments: convertToEmailAttachments(commentAttachments).map(
							(a) => JSON.stringify(a),
						),
					}),
				),
			)
			commentEditor?.commands.clearContent()
			commentEditor?.commands.blur()
			resetComment()
			setIsComment(false)
			scrollNewEventIntoView()

			return
		}
		if (activeTab === "reply") {
			if (composeEditor?.isEmpty && !attachments.length) {
				return
			}

			if (hasUnuploadedInlineImage(composeEditor?.state.doc)) {
				toast.info({
					title: "Image is still uploading",
					description: "Send once it finishes, or remove it to send now.",
					icon: IconImageFill18,
				})
				return
			}

			const channel =
				replyType === "live-chat" ? await resolveLiveChannel() : null
			if (replyType === "live-chat" && !channel) return
			const sendAs = sendChannel(replyType, channel)
			if (channel && channel !== "email") {
				const liveChatContent = composeEditor?.getHTML() ?? ""
				await claimUnassignedThread()
				sendLiveChatReply(
					channel.socket,
					{
						sessionId: channel.sessionId,
						content: liveChatContent,
						imageUrl: session?.imageUrl,
						name: session?.name,
						attachments: JSON.stringify(formattedAttachments),
						cc: ccRecipients.map((recipient) => recipient.email),
						bcc: bccRecipients.map((recipient) => recipient.email),
					},
					thread.id,
					undefined,
					(messageId) =>
						sendPendingMessage(messageId, liveChatContent, "live-chat"),
				)
			}
			if (sendAs === "email") {
				if (!workspace?.emailSettings) {
					toast.success({
						title: "Connect support email",
						description: "Please connect your support email to send messages.",
						icon: IconEnvelopePlusFill18,
						action: {
							label: "Configure email",
							callback: () => {
								void navigate({ to: "/settings/emails" })
							},
						},
					})
					return
				}
				if (!hasEnabledEmail(workspace.emailSettings)) {
					toast.info({
						title: "Email setup required",
						description: "Please set up your email before sending a message.",
						action: {
							label: "Configure email",
							callback: () => {
								void navigate({ to: "/settings/emails" })
							},
						},
					})
					return
				}
				if (isForwardMode && forwardRecipients.length === 0) {
					toast.error({ title: "Please add at least one recipient." })
					return
				}
				if (!isForwardMode && !thread.contactId) {
					toast.error({ title: "Please select a person to reply." })
					return
				}
				if (
					!isForwardMode &&
					// A custom channel reply goes to the workspace's integration, not by email.
					thread.origin !== "CUSTOM" &&
					thread.contactId &&
					thread.contact?.isEmailSuppressed
				) {
					const contactId = thread.contactId
					toast.error({
						title: "Email delivery is blocked for this contact",
						description:
							"Their address is suppressed on our email provider (hard bounce, spam complaint, or manual suppression), so a reply cannot be delivered.",
						action: {
							label: "Re-subscribe",
							callback: () => reactivateEmail.mutate({ contactId }),
						},
					})
					return
				}

				// Checked before the forward flow creates its contact and thread, so a
				// bad address leaves nothing behind.
				const invalidRecipients = invalidRecipientEmails([
					...(isForwardMode
						? forwardRecipients
						: [{ email: thread.contact?.email ?? "" }]),
					...ccRecipients,
					...bccRecipients,
				])
				if (invalidRecipients.length > 0) {
					toast.error({
						title: "Check the recipient address",
						description: `Not a valid email address: ${invalidRecipients.join(", ")}`,
					})
					return
				}

				const emailId = generateId()
				const originalContent = composeEditor?.getHTML() ?? ""

				const emailContent = composeEditor
					? prepareComposerHtmlForEmail(composeEditor.getHTML())
					: undefined

				if (isForwardMode) {
					const forwardRecipientsSnapshot = [...forwardRecipients]
					const primaryForwardRecipient = forwardRecipientsSnapshot[0]
					if (!primaryForwardRecipient) return

					let contactId: string | null = null
					const existingContact = await zero.run(
						queries.contactByEmail(primaryForwardRecipient.email),
						{ type: "complete" },
					)
					if (existingContact) {
						contactId = existingContact.id
					} else {
						const newContactId = generateId()
						await mutateOk(
							zero.mutate(
								mutators.contacts.create({
									id: newContactId,
									email: primaryForwardRecipient.email,
									name: primaryForwardRecipient.name || "",
								}),
							),
						)
						contactId = newContactId
					}

					const newThreadId = generateId()
					await mutateOk(
						zero.mutate(
							mutators.threads.create({
								id: newThreadId,
								isRead: false,
								reporterId: session?.userId ?? null,
								text: "",
								assigneeId: session?.userId ?? null,
								painLevel: "UNKNOWN",
								state: "NEW",
								origin: "EMAIL",
								attachments: [],
								replied: true,
								contactIsUnverified: false,
								lastMessageAt: Date.now(),
							}),
						),
					)

					await mutateOk(
						zero.mutate(
							mutators.threads.update({
								id: newThreadId,
								contactId,
								title,
							}),
						),
					)

					// Snapshot every routing field at send time. Retry replays this
					// against the already-created forward thread under a fresh id;
					// it must never re-read the live thread or re-run the compose
					// flow, or a failed send retried after navigating delivers to
					// the wrong recipients (PRO incident).
					const forwardSnapshot: Omit<CreateEmailArgs, "id"> = {
						contactId: contactId,
						content: emailContent ?? "",
						subject: title,
						threadId: newThreadId,
						userId: session?.userId ?? "",
						emailAddressId: fromAddressId,
						from: {
							email:
								primaryAddress(workspace.emailSettings)?.primaryEmail ?? "",
							name: `${session?.name.split(" ")[0]} (${session?.workspaceName})`,
							mailboxHash: "",
						},
						to: forwardRecipientsSnapshot,
						cc: ccRecipients,
						bcc: bccRecipients,
						attachments: formattedAttachments,
					}

					const sendForwardEmail = async (forwardEmailId: string) => {
						const forwardMutation = zero.mutate(
							mutators.emails.create({
								id: forwardEmailId,
								...forwardSnapshot,
							}),
						)

						const forwardClientRes = await forwardMutation.client
						if (forwardClientRes.type === "error") {
							toast.error({
								title: "Failed to send email",
								description: forwardClientRes.error.message,
							})
							return false
						}

						void forwardMutation.server.then((forwardResult) => {
							if (forwardResult.type === "error") {
								toast.error({
									title: "Failed to send email",
									description:
										"Your forwarded message was not delivered. Click retry to resend it to the original recipient.",
									action: {
										label: "Retry",
										callback: () => {
											void navigate({
												to: "/threads/$id",
												params: { id: forwardSnapshot.threadId },
											})
											void sendForwardEmail(generateId())
										},
									},
									time: 300000,
								})
							}
						})
						return true
					}

					const forwardSent = await sendForwardEmail(emailId)
					if (!forwardSent) return

					composeEditor?.commands.clearContent()
					composeEditor?.commands.blur()
					reset()
					// Wipe any AI draft from the source thread — the agent has
					// just consumed it by sending. cancelAiDraft is idempotent
					// (nulls aiDraftStartedAt + aiDraftHtml + aiDraftGeneratedAt).
					void zero.mutate(
						mutators.threads.cancelAiDraft({ threadId: thread.id }),
					)
					setIsReply(false)
					setHasClickedReply(false)
					setIsForwardMode(false)
					setForwardRecipients([])
					clearReplyRecipients()
					void navigate({ to: "/threads/$id", params: { id: newThreadId } })
					return
				} else {
					await claimUnassignedThread()
					sendPendingMessage(emailId, originalContent)

					const replyToRecipient: EmailContact = {
						email: thread.contact?.email ?? "",
						name: thread.contact?.name ?? "",
						mailboxHash: "",
					}

					// Snapshot every routing field at send time. Retry replays this
					// payload verbatim; it must never re-read the live thread, or a
					// failed send retried after navigating delivers to the wrong
					// recipients (PRO incident: reply sent on a different thread).
					const replySnapshot: Omit<CreateEmailArgs, "id"> = {
						contactId: thread.contactId ?? "",
						content: emailContent ?? "",
						subject: title,
						threadId: thread.id,
						userId: session?.userId ?? "",
						emailAddressId: fromAddressId,
						from: {
							email:
								primaryAddress(workspace.emailSettings)?.primaryEmail ?? "",
							name: `${session?.name.split(" ")[0]} (${session?.workspaceName})`,
							mailboxHash: "",
						},
						to: [replyToRecipient],
						cc: ccRecipients,
						bcc: bccRecipients,
						attachments: formattedAttachments,
					}

					const sendReplyEmail = async (replyEmailId: string) => {
						const unsent = useUnsentMessages.getState()
						unsent.track({
							id: replyEmailId,
							threadId: replySnapshot.threadId,
							channel: "email",
							html: replySnapshot.content,
						})
						const retry = () => {
							useUnsentMessages.getState().settle(replyEmailId)
							void sendReplyEmail(generateId())
						}

						const replyMutation = zero.mutate(
							mutators.emails.create({
								id: replyEmailId,
								...replySnapshot,
							}),
						)

						const replyClientRes = await replyMutation.client
						if (replyClientRes.type === "error") {
							unsent.fail(replyEmailId, retry)
							toast.error({
								title: "Failed to send email",
								description: replyClientRes.error.message,
							})
							return
						}

						void replyMutation.server.then((replyResult) => {
							if (replyResult.type !== "error") {
								useUnsentMessages.getState().settle(replyEmailId)
								return
							}
							// A dropped connection is not a refusal: Zero replays the write.
							if (replyResult.error.type === "zero") {
								useUnsentMessages.getState().queue(replyEmailId)
								return
							}
							useUnsentMessages.getState().fail(replyEmailId, retry)
							toast.error({
								title: "Failed to send email",
								description:
									"Your message was not delivered. Click retry to resend it to the original recipient.",
								action: {
									label: "Retry",
									// Resends the captured snapshot under a fresh id (the
									// mutator rejects a duplicate id). No live-thread reads.
									callback: () => {
										void navigate({
											to: "/threads/$id",
											params: { id: replySnapshot.threadId },
										})
										retry()
									},
								},
								time: 300000,
							})
						})
					}

					await sendReplyEmail(emailId)
				}
			}
			if (replyType === "slack") {
				if (!isNewSlackConnected) {
					toast.error({
						title: "Slack not connected",
						description: "Please connect Slack to your workspace.",
						action: {
							label: "Configure Slack integration",
							callback: () => {
								void navigate({ to: "/settings/integrations/slack" })
							},
						},
					})
					return
				}
				if (
					!thread.slackChannelId &&
					isImportedSlackChannel(latestSlackMessage?.channelId)
				) {
					toast.error({ title: IMPORTED_SLACK_REPLY_MESSAGE })
					return
				}
				if (!thread.slackChannelId && !latestSlackMessage?.channelId) {
					toast.error({
						title: "Unable to get Slack channel ID",
						description:
							"Please check your Slack connection and try to send message in new Slack thread.",
					})
					return
				}
				const htmlContent = composeEditor
					?.getHTML()
					.replaceAll("<p></p>", "<br>")
				const slackId = generateId()
				await claimUnassignedThread()
				useUnsentMessages.getState().track({
					id: slackId,
					threadId: thread.id,
					channel: "slack",
					html: htmlContent ?? "",
				})
				const slackMutation = zero.mutate(
					mutators.slackMessages.create({
						id: slackId,
						html: htmlContent ?? "",
						threadId: thread.id,
						channelId:
							thread?.slackChannelId || latestSlackMessage?.channelId || "",
						userId: session?.userId ?? "",
						attachments: convertToSlackAttachments(attachments),
						type: "OUTBOUND",
					}),
				)
				composeEditor?.commands.clearContent()
				try {
					await mutateOk(slackMutation, "server")
				} catch (error) {
					useUnsentMessages.getState().settle(slackId)
					composeEditor?.commands.setContent(htmlContent ?? "")
					toast.error({
						title: "Slack reply not sent",
						description:
							error instanceof Error
								? error.message
								: "Nothing reached Slack. Try sending it again.",
						icon: SlackIcon,
					})
					return
				}
				useUnsentMessages.getState().settle(slackId)
				toast.success({
					title: "Message sent",
					icon: MailCheckIcon,
				})
			}

			if (replyType === "teams") {
				if (!thread.chatThreadId) {
					toast.error({
						title: "Unable to send Teams message",
						description: "No Teams conversation linked to this thread.",
					})
					return
				}
				if (!workspace?.teamsSettings) {
					toast.error({
						title: "Teams not connected",
						description: "Please connect Teams to your workspace.",
						action: {
							label: "Configure Teams integration",
							callback: () => {
								void navigate({ to: "/settings/integrations/teams" })
							},
						},
					})
					return
				}
				const htmlContent = composeEditor
					?.getHTML()
					.replaceAll("<p></p>", "<br>")
				const chatMessageId = generateId()
				await claimUnassignedThread()
				await mutateOk(
					zero.mutate(
						mutators.chatMessages.create({
							id: chatMessageId,
							html: htmlContent ?? "",
							text: composeEditor?.getText() ?? "",
							threadId: thread.id,
							chatThreadId: thread.chatThreadId,
							platform: "teams",
							userId: session?.userId ?? "",
							type: "OUTBOUND",
							attachments: convertToSlackAttachments(attachments),
						}),
					),
				)
				toast.success({
					title: "Message sent to Teams",
					icon: MailCheckIcon,
				})
			}

			if (thread.state === "UNSNOOZED") {
				await mutateOk(
					zero.mutate(
						mutators.threads.update({
							id: thread.id,
							state: "NEW",
						}),
					),
				)
			}
			scrollNewEventIntoView()
			composeEditor?.commands.clearContent()
			composeEditor?.commands.blur()
			reset()
			// Wipe the AI draft on the thread now that the agent has sent
			// their reply. cancelAiDraft is idempotent and a no-op when the
			// row already has nulls.
			void zero.mutate(mutators.threads.cancelAiDraft({ threadId: thread.id }))
			if (sendAs !== "live-chat") {
				setIsReply(false)
				setHasClickedReply(false)
				setIsForwardMode(false)
				setForwardRecipients([])
				clearReplyRecipients()
			}
		}
	}

	return { send, pendingMessages, getMessageContent, getUndoMessageFn }
}
